import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Backdrop,
  Snackbar,
  Alert,
} from '@mui/material';

import TrialsCard from './TrialsCard';
import { Trial } from '../../types';
import { useTrials } from '../../context/TrialsContext';
import { sortOptions } from '../../utils';

const TrialsPage: React.FC = () => {
  const { t } = useTranslation();
  const { trials, setTrials } = useTrials();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('searchTerm') || '');
  const [organSystem, setOrganSystem] = useState(searchParams.get('organSystem') || 'all');
  const [location, setLocation] = useState(searchParams.get('location') || 'all');
  const [therapyLine, setTherapyLine] = useState(searchParams.get('therapyLine') || 'all');

  const [loading, setLoading] = useState<boolean>(true);
  const [filteredTrials, setFilteredTrials] = useState<Trial[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [organSystemsOptions, setOrganSystemsOptions] = useState<string[]>([]);
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [therapyLineOptions, setTherapyLineOptions] = useState<string[]>([]);

  // Obtención de trials (caché, sessionStorage o fetch)
  useEffect(() => {
    const fetchTrials = async () => {
      setLoading(true);
      try {
        const now = new Date().getTime();
        const cached = sessionStorage.getItem('trials');
        const cachedTime = sessionStorage.getItem('trialsFetchedAt');

        const refreshThreshold = Number(import.meta.env.VITE_TRIALS_REFRESH_MS) || 86400000;

        if (cached && cachedTime && now - parseInt(cachedTime, 10) < refreshThreshold) {
          const parsed = JSON.parse(cached);
          setTrials(parsed);
          generateFilterOptions(parsed);
          setFilteredTrials(filter(parsed, searchTerm, organSystem, location, therapyLine));
          setLoading(false);
          return;
        }

        // const response = await fetch(`${import.meta.env.VITE_LOCAL_BACKEND}/get-data`);
        const response = await fetch(`/get-data`);
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        const data: Trial[] = await response.json();

        sessionStorage.setItem('trials', JSON.stringify(data));
        sessionStorage.setItem('trialsFetchedAt', now.toString());

        setTrials(data);
        generateFilterOptions(data);
        setFilteredTrials(filter(data, searchTerm, organSystem, location, therapyLine));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTrials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTrials]);

  // Actualización de parámetros de búsqueda y filtrado de trials
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchTerm) params.searchTerm = searchTerm;
    if (organSystem !== 'all') params.organSystem = organSystem;
    if (location !== 'all') params.location = location;
    if (therapyLine !== 'all') params.therapyLine = therapyLine;
    setSearchParams(params);

    // aplicar filtro sobre trials existentes
    setFilteredTrials(filter(trials, searchTerm, organSystem, location, therapyLine));
  }, [searchTerm, organSystem, location, therapyLine, setSearchParams, trials]);

  // Mostrar Snackbar y hacer scroll to top cuando cambian los resultados (y no se está cargando)
  useEffect(() => {
    if (!loading) {
      setSnackbarOpen(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [filteredTrials, loading]);

  const filter = (data: Trial[], term: string, organ: string, loc: string, therapy: string): Trial[] => {
    const lowered = term.trim().toLowerCase();
    return data.filter((trial) => {
      try {
        const matchesKeyword =
          trial.nctId.toLowerCase().includes(lowered) ||
          trial.otherStudyId.toLowerCase().includes(lowered) ||
          trial.leadSponsor.toLowerCase().includes(lowered) ||
          trial.briefTitle.toLowerCase().includes(lowered) ||
          trial.organSystem.join(' ').toLowerCase().includes(lowered) ||
          trial.locations.some((l) => l.toLowerCase().includes(lowered)) ||
          trial.conditions.some((l) => l.toLowerCase().includes(lowered)) ||
          (Array.isArray(trial.biomarker)
            ? trial.biomarker.join(' ').toLowerCase().includes(lowered)
            : trial.biomarker?.toLowerCase().includes(lowered)) ||
          trial.keywords.some((k) => k.toLowerCase().includes(lowered));

        const matchesOrgan =
          organ === 'all' || trial.organSystem.some((os) => os.toLowerCase() === organ.toLowerCase());
        const matchesLocation =
          loc === 'all' || trial.locations.some((l) => l.toLowerCase().includes(loc.toLowerCase()));
        const matchesTherapy = 
          therapy === 'all' || trial.therapyLine.some((t) => t.toLowerCase().includes(therapy.toLowerCase()))

        return matchesKeyword && matchesOrgan && matchesLocation && matchesTherapy;
      } catch (error) {
        console.error('Error filtering trials:', error);
        return false;
      }
    });
  };

  const generateFilterOptions = (data: Trial[]) => {
    const organSet = new Set(data.flatMap((t) => t.organSystem));
    setOrganSystemsOptions(sortOptions(Array.from(organSet)));

    const locSet = new Set<string>();
    data.forEach((trial) => {
      trial.locations.forEach((l) => {
        if (l && l.toLowerCase() !== 'nan') locSet.add(l);
      });
    });
    setLocationOptions(sortOptions(Array.from(locSet)));

    const therapySet = new Set<string>();
    data.forEach((trial) => {
      trial.therapyLine.forEach((t) => {
        if (t && t.toLowerCase() !== 'nan') therapySet.add(t);
      });
    });
    setTherapyLineOptions(sortOptions(Array.from(therapySet)));
  };

  const getLocationsString = (trial: Trial): string => {
    if (!trial.locations || trial.locations.length === 0) return 'N/A';
    return trial.locations.filter((loc) => loc && loc.toLowerCase() !== 'nan').join(', ');
  };

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <Box sx={{ textAlign: 'center' }}>
          <Box
            component="img"
            src="/assets/weblogo.png"
            alt="Loading"
            sx={{
              width: 60,
              height: 60,
              mb: 2,
              animation: 'spin 2s linear infinite',
            }}
          />
          <Typography variant="h6">{t('loadingTrialList') || 'Cargando estudios clínicos...'}</Typography>
        </Box>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </Backdrop>

      <Box sx={{ p: 4 }}>
        <Box
          component="img"
          src="/assets/panon-orange.jpg"
          alt={t('headerTitle')}
          sx={{ width: '100%', maxWidth: '600px', mb: 2 }}
        />
        <Typography variant="body1" sx={{ mb: 4 }}>
          {t('searchPlaceholder')}
        </Typography>

        {error && <Typography color="error">{error}</Typography>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={2} md={1.5} xl={1.25}>
            <TextField
              label={t('keyword')}
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={2} md={1.5} xl={1.25}>
            <FormControl fullWidth>
              <InputLabel id="organ-system-label">{t('organSystem')}</InputLabel>
              <Select
                labelId="organ-system-label"
                value={organSystem}
                onChange={(e) => setOrganSystem(e.target.value)}
                label={t('organSystem')}
              >
                <MenuItem value="all">{t('all')}</MenuItem>
                {organSystemsOptions.map((system) => (
                  <MenuItem key={system} value={system}>
                    {system}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={2} md={1.5} xl={1.25}>
            <FormControl fullWidth>
              <InputLabel id="location-label">{t('location')}</InputLabel>
              <Select
                labelId="location-label"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                label={t('location')}
              >
                <MenuItem value="all">{t('all')}</MenuItem>
                {locationOptions.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={2} md={1.5} xl={1.25}>
            <FormControl fullWidth>
              <InputLabel id="therapy-line-label">{t('therapyLine')}</InputLabel>
              <Select
                labelId="therapy-line-label"
                value={therapyLine}
                onChange={(e) => setTherapyLine(e.target.value)}
                label={t('therapyLine')}
              >
                <MenuItem value="all">{t('all')}</MenuItem>
                {therapyLineOptions.map((therapy) => (
                  <MenuItem key={therapy} value={therapy}>
                    {therapy}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={1.25} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setOrganSystem('all');
                setLocation('all');
                setTherapyLine('all');
              }}
              sx={{ height: '56px', textTransform: 'none' }}
            >
              {t('clear')}
            </Button>
          </Grid>
        </Grid>

        {filteredTrials.length > 0 ? (
          <Grid container spacing={2}>
            {filteredTrials.map((trial, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <TrialsCard trial={trial} getLocationsString={getLocationsString} hideLocation />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body1">{t('noTrialsFound')}</Typography>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
          {`Found ${filteredTrials.length} results.`}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TrialsPage;
